import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setCompanies, selectCompany } from '../../features/CompanySlice';
import { fetchCompanies, updateCompanyActiveStatus } from '../../api/pocketbase';
import toast from 'react-hot-toast';

// Skeleton loading component matching Dashboard style
const SkeletonRow = () => (
  <tr className="border-b hover:bg-gray-50">
    <td className="py-4 px-6">
      <div className="h-8 bg-gray-200 rounded-lg animate-pulse w-20"></div>
    </td>
    <td className="py-4 px-6">
      <div className="h-4 bg-gray-200 rounded animate-pulse w-32"></div>
    </td>
    <td className="py-4 px-6">
      <div className="h-4 bg-gray-200 rounded animate-pulse w-40"></div>
    </td>
    <td className="py-4 px-6">
      <div className="h-4 bg-gray-200 rounded animate-pulse w-24"></div>
    </td>
    <td className="py-4 px-6">
      <div className="h-6 bg-gray-200 rounded-full animate-pulse w-16"></div>
    </td>
    <td className="py-4 px-6">
      <div className="h-6 bg-gray-200 rounded-full animate-pulse w-20"></div>
    </td>
    <td className="py-4 px-6">
      <div className="h-10 bg-gray-200 rounded-lg animate-pulse w-28"></div>
    </td>
  </tr>
);

// Dropdown component with Dashboard styling
const ActionDropdown = ({ company, onAction }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState('below');
  const buttonRef = useRef(null);

  const handleAction = (action) => {
    onAction(action, company);
    setIsOpen(false);
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.dropdown-container')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Calculate dropdown position when opening
  const handleToggleDropdown = () => {
    if (!isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 160; // Approximate height of dropdown
      
      // Check if there's enough space below
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDropdownPosition('above');
      } else {
        setDropdownPosition('below');
      }
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative dropdown-container">
      <button
        ref={buttonRef}
        onClick={handleToggleDropdown}
        className="py-2 px-4 bg-[#90c740] text-white rounded-lg hover:bg-[#7ebd2e] transition-colors duration-200 font-medium text-sm"
      >
        Actions
      </button>
      
      {isOpen && (
        <div className={`absolute left-0 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] min-w-40 ${
          dropdownPosition === 'above' 
            ? 'bottom-full mb-2' 
            : 'top-full mt-2'
        }`}>
          <button
            onClick={() => handleAction('details')}
            className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 w-full text-left transition-colors duration-200"
          >
            Details
          </button>
          <button
            onClick={() => handleAction('documents')}
            className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 w-full text-left transition-colors duration-200"
          >
            Documents
          </button>
          <button
            onClick={() => handleAction('invoices')}
            className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 w-full text-left transition-colors duration-200"
          >
            Invoices
          </button>
          <button
            onClick={() => handleAction('payments')}
            className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 w-full text-left transition-colors duration-200"
          >
            Payments
          </button>
          <button
            onClick={() => handleAction('tickets')}
            className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 w-full text-left transition-colors duration-200"
          >
            Tickets
          </button>
        </div>
      )}
    </div>
  );
};

const CompaniesList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const companies = useSelector((state) => {
        return state?.company?.companies || [];
    });
    const selectedCountry = useSelector((state) => state?.company?.selectedCountry || "");
    const [isLoading, setIsLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('active');
    const [updatingCompanyId, setUpdatingCompanyId] = useState(null);

    useEffect(() => {
        const loadCompanies = async () => {
            setIsLoading(true);
            try {
                const data = await fetchCompanies();
                dispatch(setCompanies(data));
            } catch (error) {
                console.error('Error loading companies:', error);
                toast.error('Failed to load customers.');
            } finally {
                setIsLoading(false);
            }
        };
        loadCompanies();
    }, [dispatch]);

    const filteredCompanies = companies.filter((company) => {
        const matchesCountry = !selectedCountry || selectedCountry === "Select Country"
            ? true
            : company.country === selectedCountry;
        const isActive = company.active !== false;
        const matchesStatus =
            statusFilter === 'all'
                ? true
                : statusFilter === 'active'
                ? isActive
                : !isActive;

        return matchesCountry && matchesStatus;
    });

    const renderStatusBadge = (company) => {
        const isActive = company.active !== false;
        return (
            <span className={`inline-flex px-3 py-1 text-xs sm:text-sm font-medium rounded-full ${
                isActive
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-200 text-gray-700'
            }`}>
                {isActive ? 'Active' : 'Inactive'}
            </span>
        );
    };

    const handleToggleCompanyStatus = async (company) => {
        const nextActiveState = company.active === false;
        setUpdatingCompanyId(company.id);
        try {
            await updateCompanyActiveStatus(company.id, nextActiveState);
            dispatch(setCompanies(
                companies.map((item) =>
                    item.id === company.id ? { ...item, active: nextActiveState } : item
                )
            ));
            toast.success(`Company marked as ${nextActiveState ? 'active' : 'inactive'}.`);
        } catch (error) {
            console.error('Error updating company status:', error);
        } finally {
            setUpdatingCompanyId(null);
        }
    };

    const renderToggleButton = (company) => {
        const isActive = company.active !== false;
        const isUpdating = updatingCompanyId === company.id;

        return (
            <button
                type="button"
                disabled={isUpdating}
                onClick={() => handleToggleCompanyStatus(company)}
                className={`inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                    isActive
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'bg-[#90c740]/10 text-[#6f9d2c] hover:bg-[#90c740]/20'
                }`}
            >
                {isUpdating ? 'Updating...' : isActive ? 'Make Inactive' : 'Make Active'}
            </button>
        );
    };

    const handleAction = (action, company) => {
        dispatch(selectCompany(company.id));
        
        const actionPaths = {
            details: `/companies/${company.id}/details`,
            documents: `/companies/${company.id}/documents`,
            invoices: `/companies/${company.id}/invoices`,
            payments: `/companies/${company.id}/payments`,
            tickets: `/companies/${company.id}/tickets`,
        };

        const path = actionPaths[action];
        if (path) {
            navigate(path, { state: { companyData: company } });
        }
    };

    return (
        <div className="h-full flex flex-col">
            {/* Table Header */}
            <div className="p-6 border-b border-gray-100">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h3 className="text-gray-900 font-bold text-lg">Customer List</h3>
                        <p className="text-gray-500 text-sm mt-1">
                            {isLoading ? 'Loading customers...' : `${filteredCompanies.length} customers found`}
                        </p>
                    </div>
                    <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:justify-end">
                        {[
                            { value: 'active', label: 'Active' },
                            { value: 'inactive', label: 'Inactive' },
                            { value: 'all', label: 'All' },
                        ].map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => setStatusFilter(option.value)}
                                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                                    statusFilter === option.value
                                        ? 'bg-[#90c740] text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Table Container with Scroll */}
            <div className="flex-1 overflow-hidden">
                <div className="space-y-4 p-4 md:hidden">
                    {isLoading ? (
                        Array.from({ length: 6 }).map((_, index) => (
                            <div key={index} className="rounded-xl border border-gray-200 p-4 shadow-sm">
                                <div className="space-y-3">
                                    <div className="h-5 w-32 animate-pulse rounded bg-gray-200"></div>
                                    <div className="h-4 w-full animate-pulse rounded bg-gray-100"></div>
                                    <div className="h-4 w-3/4 animate-pulse rounded bg-gray-100"></div>
                                </div>
                            </div>
                        ))
                    ) : filteredCompanies.length > 0 ? (
                        filteredCompanies.map((company) => (
                            <div key={company.id} className="rounded-xl border border-gray-200 p-4 shadow-sm">
                                <div className="flex flex-col gap-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <h4 className="truncate text-base font-semibold text-gray-900">
                                                {company.name || company.companyName}
                                            </h4>
                                            <p className="mt-1 break-words text-sm text-gray-600">{company.email || "-"}</p>
                                        </div>
                                        <span className={`inline-flex flex-shrink-0 px-3 py-1 text-sm font-medium rounded-full ${
                                            (company.noOfModule || company.modules || 0) <= 3 
                                                ? 'bg-red-100 text-red-700' 
                                                : (company.noOfModule || company.modules || 0) <= 8 
                                                ? 'bg-yellow-100 text-yellow-700' 
                                                : 'bg-[#90c740]/10 text-[#90c740]'
                                        }`}>
                                            {company.noOfModule || company.modules || 0} modules
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-1 gap-2 text-sm text-gray-600">
                                        <div>
                                            <span className="font-medium text-gray-900">Country:</span> {company.country || "-"}
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-900">Status:</span> {renderStatusBadge(company)}
                                        </div>
                                    </div>
                                    {renderToggleButton(company)}
                                    <ActionDropdown company={company} onAction={handleAction} />
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-gray-500">
                            <div className="text-lg font-medium mb-2">No customers found</div>
                            <div className="text-sm">Try adjusting your search or filters</div>
                        </div>
                    )}
                </div>

                <div className="hidden h-full overflow-auto min-h-[400px] md:block">
                    <table className="min-w-[720px] w-full">
                        <thead className="sticky top-0 bg-gray-50 z-10">
                            <tr>
                                <th className="py-4 px-6 text-left font-semibold text-gray-700 text-sm">Action</th>
                                <th className="py-4 px-6 text-left font-semibold text-gray-700 text-sm">Company Name</th>
                                <th className="py-4 px-6 text-left font-semibold text-gray-700 text-sm">Email</th>
                                <th className="py-4 px-6 text-left font-semibold text-gray-700 text-sm">Country</th>
                                <th className="py-4 px-6 text-left font-semibold text-gray-700 text-sm">Modules</th>
                                <th className="py-4 px-6 text-left font-semibold text-gray-700 text-sm">Status</th>
                                <th className="py-4 px-6 text-left font-semibold text-gray-700 text-sm">Update Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                // Skeleton loading rows
                                Array.from({ length: 8 }).map((_, index) => (
                                    <SkeletonRow key={index} />
                                ))
                            ) : filteredCompanies.length > 0 ? (
                                filteredCompanies.map((company) => (
                                    <tr key={company.id} className="border-b hover:bg-gray-50 transition-colors duration-200">
                                        <td className="py-4 px-6">
                                            <ActionDropdown 
                                                company={company} 
                                                onAction={handleAction}
                                            />
                                        </td>
                                        <td className="py-4 px-6">
                                            <div className="text-gray-900 font-medium">
                                                {company.name || company.companyName}
                                            </div>
                                        </td>
                                        <td className="py-4 px-6">
                                            <div className="text-gray-700">
                                                {company.email}
                                            </div>
                                        </td>
                                        <td className="py-4 px-6">
                                            <div className="text-gray-700">
                                                {company.country}
                                            </div>
                                        </td>
                                        <td className="py-4 px-6">
                                            <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                                                (company.noOfModule || company.modules || 0) <= 3 
                                                    ? 'bg-red-100 text-red-700' 
                                                    : (company.noOfModule || company.modules || 0) <= 8 
                                                    ? 'bg-yellow-100 text-yellow-700' 
                                                    : 'bg-[#90c740]/10 text-[#90c740]'
                                            }`}>
                                                {company.noOfModule || company.modules || 0}
                                            </span>
                                        </td>
                                        <td className="py-4 px-6">
                                            {renderStatusBadge(company)}
                                        </td>
                                        <td className="py-4 px-6">
                                            {renderToggleButton(company)}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={7} className="py-12 px-6 text-center">
                                        <div className="text-gray-500">
                                            <div className="text-lg font-medium mb-2">No customers found</div>
                                            <div className="text-sm">Try adjusting your search or filters</div>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default CompaniesList;
